import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Alert, ScrollView, Pressable, Image, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ProfileStackParamList } from '../../navigation/ProfileStackNavigator';
import { 
  PageContainer, 
  ContentContainer, 
  Text, 
  Card, 
  Button,
  Theme 
} from '../../components/ui';
import { auth } from '../../config/firebase';
import { resetOnboardingState } from '../../utils/onboardingStorage';
import { Ionicons } from '@expo/vector-icons';
import { PrivateProfileService } from '../../services/privateProfileService';
import { PrivateProfile } from '../../models/PrivateProfile';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import Toast from 'react-native-toast-message';
import { SubscriptionService } from '../../services/subscriptionService';
import { BookService } from '../../services/bookService';
import { InsightsFeedService } from '../../services/insightsFeedService';
import { errorReporting } from '../../services/errorReportingService';
import { dashboardAPI } from '../../utils/api/dashboardApi';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';

const ProfileScreen: React.FC = () => {
  useNavigationLogging('ProfileScreen');
  
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileData, setProfileData] = useState<PrivateProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isDataExpanded, setIsDataExpanded] = useState(false);
  const navigation = useNavigation<NativeStackNavigationProp<ProfileStackParamList>>();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    if (!auth.currentUser) {
      setIsLoadingProfile(false);
      return;
    }

    try {
      const result = await PrivateProfileService.getProfile(auth.currentUser.uid);
      if (result.success && result.data) {
        setProfileData(result.data);
      } else {
        console.error('Failed to fetch profile:', result.error);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleOpenChat = () => {
    // Navigate to Chat tab (parent navigator)
    const parent = navigation.getParent();
    if (parent) {
      parent.navigate('Home' as never, { screen: 'MentorChat' } as never);
    }
  };

  const handleLogout = () => {
    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Logout\n\nThis will log you out of the app and reset your session. Are you sure?');
      if (confirmed) {
        performLogout();
      }
    } else {
      Alert.alert(
        'Logout',
        'This will log you out of the app and reset your session. Are you sure?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Logout',
            style: 'destructive',
            onPress: performLogout,
          },
        ]
      );
    }
  };

  const performLogout = async () => {
    setIsLoggingOut(true);
    try {
      console.log('🚪 Logging out user...');
      const userId = auth.currentUser?.uid;
      
      // Step 1: Clear subscription data
      console.log('🧹 Clearing subscription data...');
      await SubscriptionService.logout();
      
      // Step 2: Clear service caches
      console.log('🧹 Clearing service caches...');
      await BookService.clearCache();
      InsightsFeedService.clearCache();
      dashboardAPI.clearCache();
      if (userId) {
        await PrivateProfileService.invalidateProfileCache(userId);
      }
      
      // Step 3: Clear error reporting user data
      console.log('🧹 Clearing error reporting user...');
      errorReporting.clearUser();
      
      // Step 4: Clear all AsyncStorage data (except persistent app settings)
      console.log('🧹 Clearing AsyncStorage data...');
      try {
        const allKeys = await AsyncStorage.getAllKeys();
        
        // Keys to clear (user-specific data)
        const keysToRemove = allKeys.filter(key => 
          key.startsWith('@mentorist_') || // All mentorist-prefixed keys
          key.startsWith('@mentorist/') || // Alternative prefix format
          key.includes('_profile_') ||      // Profile caches
          key.includes('_book_') ||         // Book caches
          key.includes('_insights_')        // Insights caches
        );
        
        if (keysToRemove.length > 0) {
          console.log(`🧹 Removing ${keysToRemove.length} AsyncStorage keys...`);
          await AsyncStorage.multiRemove(keysToRemove);
        }
      } catch (storageError) {
        console.error('⚠️ Error clearing AsyncStorage:', storageError);
        // Don't fail logout if AsyncStorage clearing fails
      }
      
      // Step 5: Reset onboarding state
      console.log('🧹 Resetting onboarding state...');
      await resetOnboardingState();
      
      // Step 6: Sign out from Firebase
      console.log('🚪 Signing out from Firebase...');
      if (Platform.OS === 'web') {
        // Web: Sign out from BOTH Firebase instances
        // 1. Sign out from v12 modular auth
        const { signOut } = require('firebase/auth');
        await signOut(auth);
        console.log('✅ Signed out from Firebase v12');
        
        // 2. Also sign out from v5 compat auth (if it was loaded for OAuth)
        if ((window as any).firebase?.auth) {
          await (window as any).firebase.auth().signOut();
          console.log('✅ Signed out from Firebase v5 compat');
        }
      } else {
        // Native
        const authNative = require('@react-native-firebase/auth').default;
        await authNative().signOut();
      }
      
      console.log('✅ User logged out successfully - all data cleared');
      
      // Force page reload on web to ensure state is cleared
      if (Platform.OS === 'web') {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        // On native, navigate to intro screen
        // Use reset to prevent user from going back to authenticated screens
        (navigation as any).reset({
          index: 0,
          routes: [{ name: 'IntroSlides' }],
        });
      }
    } catch (error) {
      console.error('❌ Error logging out:', error);
      setIsLoggingOut(false);
      
      if (Platform.OS === 'web') {
        window.alert(`Error: Failed to logout: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } else {
        Alert.alert('Error', `Failed to logout: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  return (
    <PageContainer>
      <ContentContainer>
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Centered Avatar Header */}
          <View style={styles.profileHeader}>
            <View style={styles.avatarContainer}>
              {profileData?.userData?.avatarUrl ? (
                <Image 
                  source={{ uri: profileData.userData.avatarUrl }} 
                  style={styles.avatarImage}
                />
              ) : (
                <Ionicons name="person-circle" size={80} color={Theme.colors.textSecondary} />
              )}
            </View>
            <Text variant="body1" style={styles.emailText}>
              {auth.currentUser?.email || 'No email connected'}
            </Text>
            <Pressable 
              onPress={() => navigation.navigate('EditProfile')}
              style={styles.editProfileButton}
            >
              <Text variant="caption" style={styles.editProfileText}>
                Edit profile
              </Text>
            </Pressable>
          </View>

          {/* Mentor Card */}
          <Card style={styles.mentorCard}>
            <View style={styles.mentorCardContent}>
              <Image 
                source={require('../../assets/mentor/mentor-default.webp')}
                style={styles.mentorImage}
                resizeMode="contain"
              />
              <View style={styles.mentorInfo}>
                <Text variant="heading3" style={styles.mentorTitle}>
                  Your Mentor
                </Text>
                <Text variant="body2" color="secondary">
                  Ready to guide you
                </Text>
              </View>
              <Pressable 
                style={styles.chatButton}
                onPress={handleOpenChat}
              >
                <Ionicons name="chatbubble-outline" size={24} color={Theme.colors.primary} />
              </Pressable>
            </View>
          </Card>

          {/* Profile Menu */}
          <View style={styles.section}>
            <Card style={styles.settingsCard}>
              <SettingsRow
                icon="star-outline"
                label="Subscription"
                onPress={() => navigation.navigate('Settings')}
              />
              <View style={styles.divider} />
              <SettingsRow
                icon="chatbubble-outline"
                label="Contact us"
                onPress={() => navigation.navigate('ContactUs')}
              />
              <View style={styles.divider} />
              <SettingsRow
                icon="information-circle-outline"
                label="About Mentorist"
                onPress={() => navigation.navigate('AboutMentorist')}
              />
              <View style={styles.divider} />
              <SettingsRow
                icon="log-out-outline"
                label="Log Out"
                onPress={handleLogout}
                showChevron={false}
                textColor={Theme.colors.error}
                disabled={isLoggingOut}
              />
            </Card>
          </View>

          {/* Debug Section */}
          {__DEV__ && (
            <View style={styles.section}>
              <Pressable onPress={() => setIsDataExpanded(!isDataExpanded)}>
                <Card style={styles.settingsCard}>
                  <View style={styles.settingItem}>
                    <View style={styles.settingIcon}>
                      <Ionicons name="code" size={20} color={Theme.colors.textSecondary} />
                    </View>
                    <Text variant="body1" style={styles.settingText}>
                      Your Data
                    </Text>
                    <Ionicons 
                      name={isDataExpanded ? "chevron-up" : "chevron-down"} 
                      size={16} 
                      color={Theme.colors.textSecondary} 
                    />
                  </View>
                </Card>
              </Pressable>

              {isDataExpanded && (
                <Card style={styles.dataCard}>
                  {isLoadingProfile ? (
                    <Text variant="body2" color="secondary">
                      Loading profile data...
                    </Text>
                  ) : profileData ? (
                    <ScrollView 
                      style={styles.dataScrollView} 
                      showsVerticalScrollIndicator={true}
                      nestedScrollEnabled={true}
                    >
                      <Text variant="caption" style={styles.dataText}>
                        {JSON.stringify(profileData, null, 2)}
                      </Text>
                    </ScrollView>
                  ) : (
                    <Text variant="body2" color="secondary">
                      No profile data available
                    </Text>
                  )}
                </Card>
              )}
            </View>
          )}

          {/* App Logo and Version */}
          <View style={styles.appInfoSection}>
            <Image 
              source={require('../../assets/logo.png')}
              style={styles.appLogo}
              resizeMode="contain"
            />
            <Text variant="caption" style={styles.versionText}>
              Version {Constants.expoConfig?.version || '6.0.1'}
              {Constants.expoConfig?.ios?.buildNumber && ` (${Constants.expoConfig.ios.buildNumber})`}
              {Constants.expoConfig?.android?.versionCode && ` (${Constants.expoConfig.android.versionCode})`}
            </Text>
          </View>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </ContentContainer>
    </PageContainer>
  );
};

interface SettingsRowProps {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  showChevron?: boolean;
  textColor?: string;
  disabled?: boolean;
}

const SettingsRow: React.FC<SettingsRowProps> = ({
  icon,
  label,
  onPress,
  showChevron = true,
  textColor,
  disabled = false,
}) => {
  return (
    <Pressable 
      style={({ pressed }) => [
        styles.settingItem,
        pressed && styles.settingItemPressed,
        disabled && styles.settingItemDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
    >
      <View style={styles.settingIcon}>
        <Ionicons 
          name={icon} 
          size={20} 
          color={textColor || Theme.colors.textSecondary} 
        />
      </View>
      <Text 
        variant="body1" 
        style={[
          styles.settingText,
          textColor && { color: textColor },
        ]}
      >
        {label}
      </Text>
      {showChevron && (
        <Ionicons name="chevron-forward" size={16} color={Theme.colors.textSecondary} />
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    // Empty - just for structure
  },
  profileHeader: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: Theme.spacing.xxxl,
    marginBottom: Theme.spacing.lg,
  },
  avatarContainer: {
    marginBottom: Theme.spacing.md,
    alignItems: 'center',
  },
  avatarImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  emailText: {
    marginBottom: Theme.spacing.sm,
    color: Theme.colors.textPrimary,
  },
  editProfileButton: {
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.md,
  },
  editProfileText: {
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  mentorCard: {
    backgroundColor: Theme.colors.primaryLight,
    marginBottom: Theme.spacing.xl,
    padding: Theme.spacing.lg,
  },
  mentorCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mentorImage: {
    width: 96,
    height: 96,
    marginRight: Theme.spacing.md,
    transform: [{ scaleX: -1 }], // Flip horizontally
  },
  mentorInfo: {
    flex: 1,
  },
  mentorTitle: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.xs,
  },
  chatButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    ...Theme.shadows.sm,
  },
  section: {
    marginBottom: Theme.spacing.xxl,
  },
  sectionTitle: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.md,
    color: Theme.colors.textPrimary,
  },
  mentorTypeCard: {
    padding: 0,
    overflow: 'hidden',
  },
  mentorTypeTile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: Theme.spacing.lg,
    backgroundColor: Theme.colors.surface,
  },
  mentorTypeTileSelected: {
    backgroundColor: Theme.colors.primaryLight,
  },
  mentorTypeTileDisabled: {
    opacity: 0.6,
  },
  mentorTypeName: {
    flex: 1,
    color: Theme.colors.textPrimary,
  },
  checkmark: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thinDivider: {
    height: 1,
    backgroundColor: Theme.colors.border,
    marginHorizontal: Theme.spacing.lg,
  },
  settingsCard: {
    padding: 0,
    overflow: 'hidden',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: Theme.spacing.lg,
  },
  settingItemPressed: {
    backgroundColor: Theme.colors.surfaceSecondary,
  },
  settingItemDisabled: {
    opacity: 0.5,
  },
  settingIcon: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: Theme.spacing.md,
  },
  settingText: {
    flex: 1,
    color: Theme.colors.textPrimary,
  },
  divider: {
    height: 1,
    backgroundColor: Theme.colors.border,
    marginHorizontal: Theme.spacing.lg,
  },
  dataCard: {
    marginTop: Theme.spacing.sm,
    padding: Theme.spacing.lg,
    backgroundColor: Theme.colors.surface,
    maxHeight: 400,
  },
  dataScrollView: {
    maxHeight: 350,
  },
  dataText: {
    fontFamily: 'Courier',
    fontSize: 11,
    lineHeight: 16,
    color: Theme.colors.textPrimary,
  },
  appInfoSection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Theme.spacing.xl,
    marginTop: Theme.spacing.lg,
  },
  appLogo: {
    width: 120,
    height: 40,
    marginBottom: Theme.spacing.md,
    opacity: 0.6,
  },
  versionText: {
    color: Theme.colors.textSecondary,
    fontSize: 12,
  },
  bottomSpacer: {
    height: Theme.spacing.xxxl,
  },
});

export default ProfileScreen;
